/* eslint-disable react/jsx-one-expression-per-line */
import React, { useContext, useMemo, useState } from 'react';
import {
  Card,
  Box,
  Stack,
  Typography,
  Button,
  IconButton,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  useTheme,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements, CardElement, useStripe, useElements,
} from '@stripe/react-stripe-js';
import { AppContext } from 'Context';
import { markSectionExplored, EXPLORED_SECTION } from 'Utils/onboardingChecklist';
import {
  axios, baseUrl, dataFetcher, endpoints, getToken, STRIPE_PUBLIC_KEY,
} from 'Api';

const MAX_CARDS = 3;

// loadStripe must be called once, at module scope — not per render.
const stripePromise = loadStripe(STRIPE_PUBLIC_KEY);

// Brand → label + accent for the little card visual. Falls back to a neutral
// slate for anything Stripe reports that isn't in the map.
const BRAND_META = {
  visa: { label: 'VISA', color: '#1a1f71' },
  mastercard: { label: 'Mastercard', color: '#c9330b' },
  amex: { label: 'AMEX', color: '#2e77bc' },
  'american express': { label: 'AMEX', color: '#2e77bc' },
  discover: { label: 'Discover', color: '#e8730a' },
  diners: { label: 'Diners', color: '#0079be' },
  jcb: { label: 'JCB', color: '#0b4ea2' },
  unionpay: { label: 'UnionPay', color: '#c21836' },
};
const brandMeta = (brand) => BRAND_META[(brand || '').toLowerCase()] || { label: (brand || 'Card').toUpperCase(), color: '#475569' };

// A small credit-card-shaped tile that doubles as the brand "type icon" — a
// gold EMV chip top-left, a soft diagonal sheen, and the brand wordmark
// bottom-right.
const CardGlyph = ({ brand }) => {
  const meta = brandMeta(brand);
  return (
    <Box
      sx={{
        position: 'relative',
        width: 52,
        height: 34,
        borderRadius: 1.25,
        flexShrink: 0,
        overflow: 'hidden',
        color: '#fff',
        background: `linear-gradient(135deg, ${meta.color} 0%, ${meta.color}d9 55%, ${meta.color}b0 100%)`,
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.18), 0 1px 3px rgba(15,23,42,0.3)',
      }}
    >
      {/* diagonal sheen */}
      <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(115deg, transparent 42%, rgba(255,255,255,0.16) 50%, transparent 58%)' }} />
      {/* EMV chip */}
      <Box
        sx={{
          position: 'absolute',
          top: 7,
          left: 7,
          width: 12,
          height: 9,
          borderRadius: 0.5,
          background: 'linear-gradient(135deg, #f7e7b0, #d9b24d)',
          boxShadow: 'inset 0 0 0 0.5px rgba(0,0,0,0.18)',
        }}
      />
      {/* brand wordmark */}
      <Typography sx={{
        position: 'absolute', bottom: 4.5, right: 7, fontSize: 8.5, fontWeight: 800, fontStyle: 'italic', letterSpacing: 0.3, lineHeight: 1,
      }}
      >
        {meta.label}
      </Typography>
    </Box>
  );
};

// ── Add-card form (inside <Elements>) — Stripe's CardElement + createToken,
// posted to the existing addPaymentMethods endpoint (two single-use tokens,
// matching the legacy flow). No raw PAN ever touches our code.
const AddCardForm = ({ onCancel, onAdded }) => {
  const theme = useTheme();
  const stripe = useStripe();
  const elements = useElements();
  const { setAppSnackbar, getSubscription } = useContext(AppContext);
  const [submitting, setSubmitting] = useState(false);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);

  const cardStyle = useMemo(() => ({
    style: {
      base: {
        color: theme.palette.text.primary,
        fontFamily: 'Montserrat, sans-serif',
        fontSize: '16px',
        '::placeholder': { color: theme.palette.text.secondary },
        iconColor: theme.palette.text.secondary,
      },
      invalid: { color: theme.palette.error.main, iconColor: theme.palette.error.main },
    },
  }), [theme]);

  const submit = async () => {
    if (!stripe || !elements) return;
    setError(null);
    setSubmitting(true);
    try {
      const cardEl = elements.getElement(CardElement);
      // Two single-use tokens — the backend consumes one to attach the method
      // and keeps the other as the alternative (mirrors the legacy add flow).
      const t1 = await stripe.createToken(cardEl);
      if (t1.error) throw new Error(t1.error.message);
      const t2 = await stripe.createToken(cardEl);
      if (t2.error) throw new Error(t2.error.message);
      const res = await dataFetcher(endpoints.addPaymentMethods, {
        card_token: t2.token.id,
        card_token_alternative: t1.token.id,
        managerCard: '0',
        zip_code: '',
      });
      if (res?.success) {
        // Ticks "Add Card" off the dashboard getting-started checklist.
        markSectionExplored(EXPLORED_SECTION.addCard);
        await getSubscription();
        setAppSnackbar({ isVisible: true, type: 'success', message: 'New card added successfully' });
        onAdded();
      } else {
        setError(res?.message || 'Card not valid.');
      }
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Card not valid.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Add a card</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
          Enter your card details. Payments are processed securely by Stripe.
        </Typography>
        <Box
          sx={{
            border: '1px solid',
            borderColor: error ? 'error.main' : 'divider',
            borderRadius: 2,
            px: 1.75,
            py: 1.75,
            bgcolor: 'background.default',
            transition: 'border-color 150ms ease',
          }}
        >
          <CardElement options={cardStyle} onReady={() => setReady(true)} onChange={(e) => setError(e.error ? e.error.message : null)} />
        </Box>
        {error && (
          <Typography variant="caption" sx={{ color: 'error.main', mt: 1, display: 'block' }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onCancel} disabled={submitting} sx={{ borderRadius: 2 }}>Cancel</Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!ready || submitting || !stripe}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ borderRadius: 2, fontWeight: 700 }}
        >
          {submitting ? 'Adding…' : 'Add card'}
        </Button>
      </DialogActions>
    </>
  );
};

// ── Edit expiry — the backend only allows updating the expiry (+ a CVC check),
// not the PAN, so this is a small MM / YY / CVC form -> updatePaymentMethods.
const EditExpiryDialog = ({ open, target, onClose }) => {
  const { setAppSnackbar, getSubscription } = useContext(AppContext);
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');
  const [cvc, setCvc] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  React.useEffect(() => {
    if (open && target) {
      setMonth(String(target.card?.exp_month || '').padStart(2, '0'));
      setYear(String(target.card?.exp_year || '').slice(-2));
      setCvc('');
      setError(null);
    }
  }, [open, target]);

  const valid = month.length >= 1 && year.length === 2 && cvc.length >= 3;

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await axios.post(`${baseUrl}/updatePaymentMethods`, {
        method_id: target.id,
        exp_month: month,
        exp_year: year,
        cvc,
        token: getToken(),
      });
      if (res?.data?.success !== false) {
        await getSubscription();
        setAppSnackbar({ isVisible: true, type: 'success', message: res?.data?.message || 'Card updated', duration: 1500 });
        onClose();
      } else {
        setError(res?.data?.message || 'Could not update the card.');
      }
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not update the card.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
        Edit card
        {target?.card?.last4 ? ` •••• ${target.card.last4}` : ''}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
          Update the expiry date on this card.
        </Typography>
        <Stack direction="row" spacing={1.5}>
          <TextField
            label="Month (MM)"
            value={month}
            onChange={(e) => setMonth(e.target.value.replace(/\D/g, '').slice(0, 2))}
            inputProps={{ inputMode: 'numeric' }}
            size="small"
            fullWidth
          />
          <TextField
            label="Year (YY)"
            value={year}
            onChange={(e) => setYear(e.target.value.replace(/\D/g, '').slice(0, 2))}
            inputProps={{ inputMode: 'numeric' }}
            size="small"
            fullWidth
          />
          <TextField
            label="CVC"
            value={cvc}
            onChange={(e) => setCvc(e.target.value.replace(/\D/g, '').slice(0, 4))}
            inputProps={{ inputMode: 'numeric' }}
            size="small"
            fullWidth
          />
        </Stack>
        {error && (
          <Typography variant="caption" sx={{ color: 'error.main', mt: 1.5, display: 'block' }}>{error}</Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={saving} sx={{ borderRadius: 2 }}>Cancel</Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={!valid || saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ borderRadius: 2, fontWeight: 700 }}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const CardRow = ({
  method, isDefault, canRemove, onSetDefault, onEdit, onRemove, busy,
}) => {
  const { card } = method;
  const now = new Date();
  const expired = card?.exp_year < now.getFullYear()
    || (card?.exp_year === now.getFullYear() && card?.exp_month < now.getMonth() + 1);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: { xs: 1.25, md: 1.75 },
        border: '1px solid',
        borderColor: isDefault ? 'primary.main' : 'divider',
        borderRadius: 1.5,
        bgcolor: isDefault ? 'action.hover' : 'transparent',
        flexWrap: { xs: 'wrap', sm: 'nowrap' },
      }}
    >
      <CardGlyph brand={card?.brand} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography sx={{ fontWeight: 700 }}>{`•••• ${card?.last4 || '••••'}`}</Typography>
          {isDefault && (
            <Chip size="small" color="primary" icon={<CheckCircleRoundedIcon />} label="Default" sx={{ height: 22, fontWeight: 700 }} />
          )}
          {expired && <Chip size="small" color="error" variant="outlined" label="Expired" sx={{ height: 22 }} />}
        </Stack>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {`Expires ${String(card?.exp_month || '').padStart(2, '0')}/${card?.exp_year || '––'}`}
        </Typography>
      </Box>
      <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
        <Tooltip title={isDefault ? 'This is your default card' : 'Set as default'}>
          <span>
            <IconButton size="small" onClick={onSetDefault} disabled={isDefault || busy} color={isDefault ? 'primary' : 'default'}>
              {isDefault ? <StarRoundedIcon fontSize="small" /> : <StarBorderRoundedIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Edit expiry">
          <span>
            <IconButton size="small" onClick={onEdit} disabled={busy}>
              <EditRoundedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={canRemove ? 'Remove card' : 'Add another card before removing this one'}>
          <span>
            <IconButton size="small" onClick={onRemove} disabled={!canRemove || busy} color="error">
              <DeleteOutlineRoundedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Box>
  );
};

// Full-width, in-place payment-methods manager. Rendered on the billing page
// (replacing the plan + billing-info sections) instead of routing to a
// separate page.
export const PaymentMethodsManager = ({ onClose, autoAdd = false }) => {
  const { subscriptionDetails, getSubscription, setAppSnackbar } = useContext(AppContext);
  const methods = subscriptionDetails?.paymentMethods || [];
  const defaultId = subscriptionDetails?.default_payment_methods;

  const [addOpen, setAddOpen] = useState(autoAdd);
  const [editTarget, setEditTarget] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const atLimit = methods.length >= MAX_CARDS;
  const canRemove = methods.length > 1;

  const runAction = async (id, endpoint, successMsg) => {
    setBusyId(id);
    try {
      const res = await dataFetcher(endpoint, { payment_method_id: id });
      if (res?.success) {
        await getSubscription();
        setAppSnackbar({ isVisible: true, type: 'success', message: successMsg });
      } else {
        setAppSnackbar({ isVisible: true, type: 'error', message: res?.message || 'Something went wrong.' });
      }
    } catch (e) {
      setAppSnackbar({ isVisible: true, type: 'error', message: e?.response?.data?.message || 'Something went wrong.' });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card sx={{
      p: { xs: 2, md: 3 }, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column',
    }}
    >
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: 0 }}>
          <IconButton size="small" onClick={onClose} sx={{ mr: 0.5 }}>
            <ArrowBackRoundedIcon fontSize="small" />
          </IconButton>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 18 }} noWrap>Payment methods</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {`${methods.length} of ${MAX_CARDS} cards`}
            </Typography>
          </Box>
        </Stack>
        <Tooltip title={atLimit ? `You can keep up to ${MAX_CARDS} cards` : ''}>
          <span>
            <Button
              variant="contained"
              startIcon={<AddRoundedIcon />}
              onClick={() => setAddOpen(true)}
              disabled={atLimit}
              sx={{ borderRadius: 2, fontWeight: 700 }}
            >
              Add card
            </Button>
          </span>
        </Tooltip>
      </Stack>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {methods.length === 0 ? (
          <Stack spacing={2} sx={{ alignItems: 'center', justifyContent: 'center', py: 6, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>No cards on file yet.</Typography>
            <Button variant="outlined" startIcon={<AddRoundedIcon />} onClick={() => setAddOpen(true)} sx={{ borderRadius: 2 }}>
              Add your first card
            </Button>
          </Stack>
        ) : (
          <Stack spacing={1.5}>
            {methods.map((m) => (
              <CardRow
                key={m.id}
                method={m}
                isDefault={defaultId === m.id}
                canRemove={canRemove}
                busy={busyId === m.id}
                onSetDefault={() => runAction(m.id, endpoints.setDefaultPaymentMethods, 'Default payment method updated')}
                onEdit={() => setEditTarget(m)}
                onRemove={() => runAction(m.id, endpoints.removePaymentMethods, 'Card removed')}
              />
            ))}
          </Stack>
        )}
        {!canRemove && methods.length === 1 && (
          <Alert severity="info" variant="outlined" sx={{ borderRadius: 1.5, mt: 2 }}>
            Add a second card before removing this one.
          </Alert>
        )}
      </Box>

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <Elements stripe={stripePromise}>
          <AddCardForm onCancel={() => setAddOpen(false)} onAdded={() => setAddOpen(false)} />
        </Elements>
      </Dialog>

      <EditExpiryDialog open={Boolean(editTarget)} target={editTarget} onClose={() => setEditTarget(null)} />
    </Card>
  );
};

export default PaymentMethodsManager;
